import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { AddFilesModal, type AddFilesModalInitialTab } from './AddFilesModal'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import {
  selectAgentV2SendableChatFileIds,
  selectAgentV2SendableChatWritingSampleFileId,
  selectAgentV2ConversationMatterId,
} from '@/agent-v2/conversations/store/selectors'
import { selectAgentConversationFormUserFileIds, selectAgentConversationFormWritingSampleFileIds } from '@/agent-v2/chat-form/store/selectors'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { RootState } from '@/store/store'
import { selectFilesById } from '@/user_files/files/store/selectors'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { kToastDurationMediumMs } from '@/constants/constants-toast'

function getAlreadyInChatMessage(fileNames: (string | undefined)[]): string {
  const [onlyFileName] = fileNames
  if (fileNames.length === 1 && onlyFileName) return `${onlyFileName} is already in this chat`
  return fileNames.length === 1 ? '1 file is already in this chat' : `${fileNames.length} files are already in this chat`
}

type AssistantDocumentSelectionDialogProps = {
  open: boolean
  onClose: (value: boolean) => void
  conversationId: string
  isTemplateMode: boolean
  initialAddFilesTab?: AddFilesModalInitialTab
}

/**
 * AssistantDocumentSelectionDialog component.
 *
 * This component is used exclusively in the Assistant features
 * to allow the user to select Matter Files for the Assistant.
 *
 * @param props - The properties for the AssistantDocumentSelectionDialog component.
 * @returns A JSX element representing the document selection dialog.
 */
export default function AssistantDocumentSelectionDialog(props: AssistantDocumentSelectionDialogProps) {
  const { open, onClose, conversationId, isTemplateMode } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const prevOpenRef = useRef<boolean>(open)
  const selectionMadeRef = useRef<boolean>(false)
  const conversationMatterId = useAppSelector((state: RootState) => selectAgentV2ConversationMatterId(state, { conversationId }))
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const effectiveMatterId = conversationMatterId ?? activeMatterId
  // The picker controls the composer's files. The chat's files already go out with every message, so picking one
  // again leaves it out of the composer and says so in a toast.
  const composerFileIds = useAppSelector((state: RootState) => selectAgentConversationFormUserFileIds(state, { conversationId }))
  const composerWritingSampleFileIds = useAppSelector((state: RootState) => selectAgentConversationFormWritingSampleFileIds(state, { conversationId }))
  const chatFileIds = useAppSelector((state: RootState) => selectAgentV2SendableChatFileIds(state, { conversationId }))
  const chatWritingSampleFileId = useAppSelector((state: RootState) => selectAgentV2SendableChatWritingSampleFileId(state, { conversationId }))
  const filesById = useAppSelector(selectFilesById)

  const showAlreadyInChatToast = (fileIds: string[]) => {
    dispatch(
      openGlobalToast({
        type: GlobalToastType.INFO,
        message: getAlreadyInChatMessage(fileIds.map((id) => filesById[id]?.original_filename)),
        durationMs: kToastDurationMediumMs,
      })
    )
  }

  const handleFilesSelected = (userFileIds: string[]) => {
    const alreadyInChatFileIds = userFileIds.filter((id) => chatFileIds.includes(id))
    dispatch(AgentConversationFormActions.setUserFiles({ conversationId, userFileIds: userFileIds.filter((id) => !chatFileIds.includes(id)) }))
    if (alreadyInChatFileIds.length === 0) return { showedToast: false }

    // The global toast only shows one message at a time, so the next one replaces it. Saying a picked file wasn't added
    // matters more than the picker's empty folder note, which it skips when this one shows.
    showAlreadyInChatToast(alreadyInChatFileIds)
    return { showedToast: true }
  }

  // analytics: drive dialog opened in template mode
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      selectionMadeRef.current = false
      if (isTemplateMode) {
        trackEvent(AnalyticsEvent.TemplateDriveDialogOpened, { conversationId })
      }
    }
    // dialog closed
    if (prevOpenRef.current && !open && isTemplateMode && !selectionMadeRef.current) {
      trackEvent(AnalyticsEvent.TemplateSourceDialogDismissed, { conversationId })
    }
    prevOpenRef.current = open
  }, [open, isTemplateMode, conversationId, trackEvent])

  if (!isTemplateMode) {
    return effectiveMatterId ? (
      <AddFilesModal
        open={open}
        onClose={() => onClose(false)}
        matterId={effectiveMatterId}
        initialSelectedFileIds={composerFileIds}
        initialTab={props.initialAddFilesTab}
        onFilesSelected={handleFilesSelected}
      />
    ) : null
  }

  return effectiveMatterId ? (
    <AddFilesModal
      open={open}
      onClose={() => onClose(false)}
      matterId={effectiveMatterId}
      title="Add writing sample"
      selectionMode="single"
      initialSelectedFileIds={composerWritingSampleFileIds}
      initialTab={props.initialAddFilesTab}
      onFilesSelected={([userFileId]) => {
        // Single-select: a chosen file sets the sample; confirming with nothing selected clears it.
        selectionMadeRef.current = true
        if (userFileId && userFileId !== chatWritingSampleFileId) {
          dispatch(AgentConversationFormActions.addWritingSampleFile({ conversationId, userFileId }))
          return { showedToast: false }
        }

        dispatch(AgentConversationFormActions.clearWritingSampleFiles({ conversationId }))
        if (!userFileId) return { showedToast: false }

        showAlreadyInChatToast([userFileId])
        return { showedToast: true }
      }}
    />
  ) : null
}
